import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FaPlus, FaMinus, FaShare, FaMapMarkerAlt, FaCalendar, FaLink, FaChevronUp, FaEnvelope, FaCog, FaShoppingCart, FaEllipsisH, FaLock } from 'react-icons/fa';
import UserActionsMenu from '@/components/UserActionsMenu/UserActionsMenu';
import { ProfileSpotlightButton } from '@/components/ProfileSpotlightButton';
import { useAppDispatch, useAppSelector } from '@/redux/Store';
import { toggleFollow } from '@/redux/thunks/dashboardThunks';
import { purchaseProfile, fetchUserBalance } from '@/redux/thunks/marketplaceThunks';
import { fetchProfileFees, fetchPriceHistory, fetchProvenance, fetchTopHashtags } from '@/redux/thunks/profileThunks';
import { selectProfileFees, selectPriceHistory, selectProvenance, selectTopHashtags } from '@/redux/slices/profileSlice';
import { fetchAchievements } from '@/redux/thunks/achievementThunks';
import { useSignupGuard } from '@/hooks/useSignupGuard';
import PriceChart from './components/PriceChart';
import ProvenanceTimeline from './components/ProvenanceTimeline';
import BadgeTooltip from './components/BadgeTooltip';
import { LoadingSpinner } from '@/components';
import PurchaseModal from '@/app/marketplace/components/PurchaseModal';
import PaywallModal from '@/components/Post/PaywallModal';
import { ProfileListing } from '@/app/marketplace/components/ProfileCard';
import { toast } from 'react-toastify';
import UserPostsFeed from './components/UserPostsFeed';
import { getCachedShareUrl, prefetchShareToken, recordShareClickIfPresent, stripTrkrFromUrl, copyToClipboard } from '@/utils/shareLink';
import { CreatePostFAB } from '@/components/Post';
import FollowListModal from './components/FollowListModal';
import UserNFTProfiles from './components/UserNFTProfiles';
import ProfileAccessPanel from './components/ProfileAccessPanel';
import AchievementTokens from './components/AchievementTokens';
import ProfileReputationCard from './components/ProfileReputationCard';
import { BotBadge } from '@/components/BotBadge';
import { AgentBadge } from '@/components/AgentBadge';
import { AgentVerificationBadges } from '@/components/AgentVerificationBadges';
import { AgentCapabilityChips } from '@/components/AgentCapabilityChips';
import { AgentChainChips } from '@/components/AgentChainChips';
import { labelForChain } from '@/components/AgentChainChips/agentChainLabels';
import { AgentReceiptList } from '@/components/AgentReceiptList';
import { AgentAchievements } from '@/components/AgentAchievements';
import { formatEndpointPrice } from '@/utils/formatEndpointPrice';
import { KlyxOperatedBadge } from '@/components/KlyxOperatedBadge';
import { Post } from '@/redux/slices/dashboardSlice';
import { formatName, formatHandle, nameInitial } from '@/utils/formatName';
import { formatRelativeTime } from '@/utils/formatTime';
import api from '@/utils/api';
import { getMediaUrl } from '@/constants/media';
import './page.scss';

interface UserProfile {
  userId: string;
  username: string;
  /** Server-driven bot flag (see docs/klyx_bots.md). Drives the
   *  "🤖 Bot" disclosure badge next to the display name. */
  isBot?: boolean;
  /** Server-driven agent flag (see docs/klyx_agents.md). Drives the
   *  "🤖 Agent" disclosure badge next to the display name. Distinct
   *  from isBot — bots and agents are separate primitives. */
  isAgent?: boolean;
  /** Ring 2 agent capability declarations (empty for humans / bots,
   *  possibly empty for agents that skipped the picker at register). */
  capabilities?: string[];
  /** Chains the agent operates on. Same story as capabilities:
   *  empty for non-agents; possibly empty for agents that haven't
   *  declared any yet. */
  chains?: string[];
  /** Ring 2.5 agent endpoints (empty for non-agents / agents without
   *  any declared endpoints yet). */
  endpoints?: Array<{
    id: string;
    label: string | null;
    url: string;
    description: string | null;
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    priceMinSmallest: string;
    priceMaxSmallest: string;
    priceAsset: string;
    createdAt: string;
  }>;
  /** Ring 3 Phase 1 raw receipt count across all lifecycle states.
   *  0 for non-agents and for agents that have not been invoked yet. */
  receiptCount?: number;
  /** ADR-015 D8 platform-run flag. True only for the small set of
   *  Klyx-operated agents. Drives the "Operated by Klyx" chip in the
   *  hero and the About Klyx-run agents commitment section on the
   *  profile. Absent for non-agents. */
  isKlyxOperated?: boolean;
  /** Per-chain wallets. Ordered chain-first-then-primary-first by
   *  the server; take the first row of each chain for the "primary"
   *  display. Empty for non-agents. */
  wallets?: Array<{
    id: string;
    agentUserId: string;
    chain: string;
    address: string;
    isPrimary: boolean;
    label: string | null;
    createdAt: string;
  }>;
  /** ADR-012 verification rows (public shape — email targetValue
   *  is domain-only; revoked rows filtered server-side). Empty for
   *  non-agents. Detailed badge display lands in PR H. */
  verifications?: Array<{
    id: string;
    type: 'email' | 'endpoint' | 'dns_txt';
    targetValue: string;
    status: 'pending' | 'verified' | 'stale';
    verifiedAt: string | null;
    staleSince: string | null;
  }>;
  /** Derived count of endpoint verifications in `verified` or `stale`
   *  state — feeds the AgentAchievements chip that was scaffolded in
   *  PR #582. 0 for non-agents and for agents with no endpoint
   *  verifications yet. */
  verifiedEndpointCount?: number;
  displayName: string;
  avatar: string;
  banner: string;
  bio: string;
  /** One-line teaser shown under the display name in the hero. The
   *  long-form bio renders inside the About section. */
  headline?: string;
  location: {
    home: string;
    touring: string;
  };
  joinedDate: string;
  /** Most-recent login timestamp. Surfaced as "Active X ago" on the
   *  About card; matches the discovery card's activity indicator. */
  lastSeen?: string | null;
  links: Array<{ label: string; url: string }>;
  interests: string[];
  stats: {
    posts: number;
    followers: number;
    following: number;
    profileViews: number;
  };
  klyxStats: {
    value: number;
    trending: number;
    currentPrice: number;
  };
  badges: Array<{
    id: string;
    name: string;
    icon: string;
    description: string;
  }>;
  isFollowing: boolean;
  mutualConnections: number;
  pinnedPost: Post | null;
  profileProvenance: {
    creatorDisplayName: string;
    creatorUsername: string;
    ownerDisplayName: string;
    ownerUsername: string;
  } | null;
}

const ProfilePage: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth?.user);
  const userBalance = useAppSelector((state) => state.marketplace?.userBalance || 0);

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Tab persists in the URL so refreshes, back/forward, and shared
  // links all land on the same tab. Defaults to 'posts' when the
  // param is missing or invalid.
  //
  // Phase B of the profile redesign (#255): the old sidebar dissolved
  // into two new tabs — About (identity + social) and Trading (price,
  // chart, provenance, paid access). Old 'profiles' tab renamed to
  // 'nfts' to match what it actually shows.
  // #299: About tab dissolved into the main view — the identity +
  // social block now renders unconditionally above the tabs. The
  // tab union shrank to Posts / NFTs / Trading and the default
  // landing flipped to Posts. Stale ?tab=about URLs fall through
  // the VALID_TABS gate and land on Posts.
  type TabType = 'posts' | 'nfts' | 'trading';
  const VALID_TABS: readonly TabType[] = ['posts', 'nfts', 'trading'];
  const DEFAULT_TAB: TabType = 'posts';
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: TabType =
    (VALID_TABS as readonly string[]).includes(tabParam ?? '') ? (tabParam as TabType) : DEFAULT_TAB;
  const setActiveTab = (tab: TabType) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === DEFAULT_TAB) next.delete('tab');
        else next.set('tab', tab);
        return next;
      },
      { replace: true }, // don't pile up history entries on tab clicks
    );
  };

  // Read source attribution (from/fromId) once at mount, then strip the
  // params so a shared link doesn't carry them. Stored in a ref so the
  // sendView path below can use them without re-reading the URL.
  const sourceRef = React.useRef<{ source: string; ref?: string }>({
    source: searchParams.get('from') || 'direct',
    ref: searchParams.get('fromId') || undefined,
  });
  useEffect(() => {
    if (searchParams.has('from') || searchParams.has('fromId')) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('from');
          next.delete('fromId');
          return next;
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only strip
  }, []);

  // Record share click + strip ?trkr= once per mount (#256). Fire and
  // forget — failures don't block the page.
  useEffect(() => {
    recordShareClickIfPresent().finally(stripTrkrFromUrl);
  }, []);

  // Warm the share-token cache so the Share button can copy
  // synchronously without awaiting (#256).
  useEffect(() => {
    if (!username) return;
    prefetchShareToken({ contentType: 'profile', contentId: username });
  }, [username]);
  const [showAllBadges, setShowAllBadges] = useState(false);
  const [followModalType, setFollowModalType] = useState<'followers' | 'following' | null>(null);
  const [viewStartTime] = useState(Date.now());
  const [nftProfile, setNftProfile] = useState<any>(null);
  const [selectedProfileForPurchase, setSelectedProfileForPurchase] = useState<ProfileListing | null>(null);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [accessStatus, setAccessStatus] = useState<{ hasViewAccess: boolean; hasSubscription: boolean } | null>(null);
  // Links + Services & Fees collapsibles were removed in the About
  // redesign (#299) — each lives in its own grid card now so the
  // card width is the natural height constraint.

  // Strip @ from username for comparison
  const usernameWithoutAt = username?.startsWith('@') ? username.substring(1) : username;
  const isOwnProfile = currentUser?.username === usernameWithoutAt;

  const profileFees = useAppSelector((state) =>
    profile?.userId ? selectProfileFees(profile.userId)(state) : null
  );
  // Creator has at least one monetised access path — gates both the
  // hero Subscribe button and the Trading-tab ProfileAccessPanel.
  const hasMonetization = !!profileFees && (
    parseFloat(profileFees.viewFee || '0') > 0 ||
    parseFloat(profileFees.subscriptionFee || '0') > 0
  );
  const priceHistory = useAppSelector((state) =>
    usernameWithoutAt ? selectPriceHistory(usernameWithoutAt)(state) : null
  );
  const provenance = useAppSelector((state) =>
    usernameWithoutAt ? selectProvenance(usernameWithoutAt)(state) : null
  );
  const topHashtags = useAppSelector((state) =>
    usernameWithoutAt ? selectTopHashtags(usernameWithoutAt)(state) : null
  );

  // Fetch profile data
  useEffect(() => {
    const fetchProfile = async () => {
      if (!username) return;

      setIsLoading(true);
      try {
        const response = await api.get(`/profile/${username}`);
        setProfile(response.data);
      } catch (error) {
        console.error('Failed to fetch profile:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [username]);

  // Fetch user's primary NFT profile (for buying on others' profiles, and for PPV composer on own profile)
  useEffect(() => {
    const fetchNFTProfile = async () => {
      if (!username) return;

      try {
        const response = await api.get(`/profile/${username}/nft-profiles`, {
          params: { limit: 1, offset: 0, as: 'creator' }
        });
        if (response.data.profiles && response.data.profiles.length > 0) {
          setNftProfile(response.data.profiles[0]);
        }
      } catch (error) {
        console.error('Failed to fetch NFT profile:', error);
      }
    };

    fetchNFTProfile();
  }, [username]);

  // Fetch user balance if logged in
  useEffect(() => {
    if (currentUser && !isOwnProfile) {
      dispatch(fetchUserBalance());
    }
  }, [currentUser, isOwnProfile, dispatch]);

  // Fetch creator's on-chain fees
  useEffect(() => {
    if (profile) {
      dispatch(fetchProfileFees(profile.userId));
    }
  }, [profile?.userId, dispatch]);

  // Whether the *current* viewer already has paid access (view fee or
  // active subscription) to the creator. Drives the hero Subscribe
  // button visibility — once paid, the lock disappears immediately
  // because refreshAccessStatus is also called from the modal's
  // onUnlocked callback. Best-effort: a failed fetch leaves status null
  // and the button stays visible (better than hiding access prematurely).
  const refreshAccessStatus = useCallback(async () => {
    if (!currentUser || isOwnProfile || !nftProfile?.nftProfileId) return;
    try {
      const { data } = await api.get(`/blockchain-profiles/${nftProfile.nftProfileId}/my-access`);
      setAccessStatus({
        hasViewAccess: !!data.hasViewAccess,
        hasSubscription: !!data.hasSubscription,
      });
    } catch {
      setAccessStatus(null);
    }
  }, [currentUser, isOwnProfile, nftProfile?.nftProfileId]);

  useEffect(() => {
    refreshAccessStatus();
  }, [refreshAccessStatus]);

  // Fetch price/trade history, provenance, top hashtags, and achievements
  useEffect(() => {
    if (usernameWithoutAt) {
      dispatch(fetchPriceHistory(usernameWithoutAt));
      dispatch(fetchProvenance(usernameWithoutAt));
      dispatch(fetchTopHashtags(usernameWithoutAt));
      dispatch(fetchAchievements(usernameWithoutAt));
    }
  }, [usernameWithoutAt, dispatch]);

  // Track profile view.
  //
  // The view fires when the user leaves the page through ANY route:
  //   - SPA navigation away (React unmount)
  //   - Tab close / hard refresh (pagehide event)
  //   - Tab switch / app background (visibilitychange → hidden)
  //
  // Each path goes through the same `sendView` helper, which:
  //   1. Is no-op for the user's own profile
  //   2. Requires the same 30s threshold the server enforces
  //   3. Uses fetch with keepalive so unload-time POSTs still complete
  //      (axios doesn't expose keepalive)
  //   4. Guards with sentRef so the same mount can't fire twice (e.g.
  //      tab hide + return + close would send once, not twice)
  //
  // Server dedupes by (viewer, viewed, day), so even if the client
  // fires extra POSTs (different mount, different day) it's safe — but
  // we still guard locally to avoid pointless network calls.
  const sentRef = React.useRef(false);
  useEffect(() => {
    sentRef.current = false; // reset on profile change
  }, [username]);

  useEffect(() => {
    const sendView = () => {
      if (sentRef.current || isOwnProfile || !username) return;
      const timeSpent = Math.floor((Date.now() - viewStartTime) / 1000);
      // Server enforces the same floor (MIN_TRACKED_SECONDS) but we
      // gate client-side too so we don't fire pointless POSTs.
      if (timeSpent < 7) return;
      sentRef.current = true;

      const token = localStorage.getItem('accessToken');
      const { source, ref } = sourceRef.current;
      try {
        fetch(
          `${import.meta.env.VITE_API_URL || '/api'}/profile/${username}/view`,
          {
            method: 'POST',
            keepalive: true,
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              timeSpent,
              source,
              ...(ref ? { sourceRef: ref } : {}),
            }),
          },
        ).catch(() => {});
      } catch {
        // Some older browsers throw synchronously when keepalive isn't supported;
        // swallow because tracking is non-critical.
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') sendView();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pagehide', sendView);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pagehide', sendView);
      sendView(); // SPA-nav unmount path
    };
  }, [username, isOwnProfile, viewStartTime]);


  const { requireAuth } = useSignupGuard();

  const handleFollow = async () => {
    if (!profile) return;
    if (!requireAuth('Sign up to follow creators')) return;

    // Capture wasFollowing BEFORE the dispatch so the toast label
    // reflects the transition we just committed to. Also drives the
    // optimistic setProfile below.
    const wasFollowing = profile.isFollowing;
    const displayName = profile.displayName || profile.username;

    try {
      await dispatch(toggleFollow({
        targetUserId: profile.userId,
        user: {
          userId: profile.userId,
          username: profile.username,
          displayName: profile.displayName,
          avatar: profile.avatar,
        }
      })).unwrap();
      setProfile({
        ...profile,
        isFollowing: !wasFollowing,
        stats: {
          ...profile.stats,
          followers: profile.stats.followers + (wasFollowing ? -1 : 1),
        },
      });
      toast.success(wasFollowing ? `Unfollowed ${displayName}` : `Following ${displayName}`);
    } catch (error) {
      console.error('Failed to toggle follow:', error);
      toast.error(wasFollowing ? 'Failed to unfollow' : 'Failed to follow');
    }
  };

  const handleBuyProfile = async () => {
    if (!requireAuth('Sign up to buy creator profiles')) return;
    // Fetch the latest NFT profile data before opening modal to ensure current price
    try {
      console.log('[ProfilePage] Fetching fresh profile data before purchase...');
      const response = await api.get(`/profile/${username}/nft-profiles`, {
        params: { limit: 1, offset: 0, as: 'creator' }
      });
      if (response.data.profiles && response.data.profiles.length > 0) {
        const freshProfile = response.data.profiles[0];
        // console.log('[ProfilePage] Fresh profile data:', {
        //   id: freshProfile.id,
        //   nftProfileId: freshProfile.nftProfileId,
        //   price: freshProfile.price,
        //   ownerId: freshProfile.owner.userId
        // });
        setNftProfile(freshProfile);

        // Convert NFT profile to ProfileListing format for the modal
        const profileListing: ProfileListing = {
          id: freshProfile.id,
          creatorId: freshProfile.creator.userId,
          creatorUsername: freshProfile.creator.username,
          creatorDisplayName: freshProfile.creator.displayName,
          creatorAvatarUrl: freshProfile.creator.avatar || undefined,
          ownerId: freshProfile.owner.userId,
          ownerUsername: freshProfile.owner.username,
          ownerDisplayName: freshProfile.owner.displayName,
          ownerAvatarUrl: freshProfile.owner.avatar || undefined,
          price: freshProfile.price,
          klyxValue: freshProfile.klyxValue,
          trendingScore: 0,
          purchaseCount: freshProfile.purchaseCount,
          creatorLocation: null,
          badges: {
            verified: false,
            topProfile: false,
            trending: false,
          },
        };

        setSelectedProfileForPurchase(profileListing);
      } else {
        toast.error('Profile is no longer available for purchase');
      }
    } catch (error) {
      console.error('Failed to fetch NFT profile:', error);
      toast.error('Failed to load profile data');
    }
  };

  const handlePurchaseConfirm = async (profile: ProfileListing, customPrice: number) => {
    setIsPurchasing(true);
    try {
      await dispatch(purchaseProfile({
        profileId: profile.id,
        customPrice,
      })).unwrap();

      toast.success('Profile purchased successfully! 🎉');
      setSelectedProfileForPurchase(null);

      // Refresh the NFT profile data
      const response = await api.get(`/profile/${username}/nft-profiles`, {
        params: { limit: 1, offset: 0, as: 'creator' }
      });
      if (response.data.profiles && response.data.profiles.length > 0) {
        setNftProfile(response.data.profiles[0]);
      }
    } catch (error: any) {
      console.error('Purchase failed:', error);
      toast.error(error || 'Failed to purchase profile');
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleShare = async () => {
    // Synchronous URL with a signed `?trkr=` token if the cache is
    // warm (#256). The cache is warmed via prefetchShareToken inside
    // a mount-effect below — by the time the user clicks Share the
    // token is usually ready, and even if it isn't the URL still
    // works (just no attribution).
    const handle = username ?? '';
    const url = getCachedShareUrl({ contentType: 'profile', contentId: handle });
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${formatName({ displayName: profile?.displayName, username: username ?? undefined })} on Klyx`,
          url,
        });
        return;
      } catch (error) {
        // User cancelled the OS share sheet — silently fall through
        // to clipboard so they can still get a copy of the link.
        console.warn('[share] navigator.share cancelled / failed:', error);
      }
    }
    try {
      await copyToClipboard(url);
      toast.success('Profile link copied to clipboard!');
    } catch (err) {
      console.error('[share] copy failed:', err);
      toast.error('Could not copy link');
    }
  };

  const handleMessage = async () => {
    if (!profile) return;
    if (!requireAuth('Sign up to message creators')) return;

    try {
      // Get or create thread with this user
      const response = await api.post('/messages/threads/start', {
        otherUserId: profile.userId,
      });

      // Navigate to messages page with thread selected
      navigate(`/messages?thread=${response.data.threadId}`);
    } catch (error) {
      console.error('Failed to start conversation:', error);
      toast.error('Failed to start conversation. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="profile-page">
        <LoadingSpinner size="medium" message="Loading profile..." fullHeight />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="profile-page">
        <div className="profile-not-found">
          <h2>Profile Not Found</h2>
          <p>The profile you're looking for doesn't exist.</p>
          <button onClick={() => navigate('/dashboard')}>Go to Dashboard</button>
        </div>
      </div>
    );
  }

  const visibleBadges = showAllBadges ? profile.badges : profile.badges.slice(0, 4);
  const hiddenBadgesCount = profile.badges.length - 4;

  const feeItems = profileFees ? [
    { label: 'DM', value: profileFees.dmFee },
    { label: 'View', value: profileFees.viewFee },
    { label: 'Live Session', value: profileFees.liveSessionFee },
    { label: 'Consultation', value: profileFees.consultationFee },
    { label: 'Subscription', value: profileFees.subscriptionFee },
  ].filter(f => parseFloat(f.value) > 0) : [];

  return (
    <div className="profile-page">
      {/* ── HERO ────────────────────────────────────────────────────────
          Full-width identity strip. Cover image lives behind a scrim;
          avatar + name + handle + location + bio + primary actions
          all in one cohesive block. Replaces the old `.profile-cover`
          (separate banner) + `.sidebar-avatar-row` (separate avatar +
          name + buttons buried in the sidebar). Profile page redesign
          phase 1 — see #255. */}
      <header className="profile-hero">
        <div className="profile-hero__bg" aria-hidden="true">
          {profile.banner ? (
            <img src={getMediaUrl(profile.banner) || undefined} alt="" className="profile-hero__bg-image" />
          ) : (
            <div className="profile-hero__bg-gradient" />
          )}
          <div className="profile-hero__scrim" />
        </div>
        <div className="profile-hero__content">
          <div className="profile-hero__identity">
            <div className="profile-hero__avatar">
              {profile.avatar ? (
                <img src={getMediaUrl(profile.avatar) || undefined} alt={profile.username} />
              ) : (
                <div className="profile-hero__avatar-placeholder">
                  {nameInitial({ displayName: profile.displayName, username: profile.username })}
                </div>
              )}
            </div>
            <div className="profile-hero__text">
              <h1 className="profile-hero__name">
                {formatName({ displayName: profile.displayName, username: profile.username })}
                <BotBadge isBot={profile.isBot} />
                <AgentBadge isAgent={profile.isAgent} />
                <KlyxOperatedBadge isKlyxOperated={profile.isKlyxOperated} />
              </h1>
              <div className="profile-hero__meta">
                <span className="profile-hero__handle">{formatHandle(profile.username)}</span>
                {profile.location.home && (
                  <>
                    <span className="profile-hero__dot">·</span>
                    <span className="profile-hero__location">
                      <FaMapMarkerAlt /> {profile.location.home}
                    </span>
                  </>
                )}
                {profile.location.touring && (
                  <span className="profile-hero__touring">
                    Touring {profile.location.touring}
                  </span>
                )}
              </div>
              {profile.headline && (
                <p className="profile-hero__bio">{profile.headline}</p>
              )}
              {profile.isAgent && profile.capabilities && profile.capabilities.length > 0 && (
                <AgentCapabilityChips
                  capabilities={profile.capabilities}
                  size="md"
                  className="profile-hero__caps"
                />
              )}
              {profile.isAgent && profile.chains && profile.chains.length > 0 && (
                <AgentChainChips
                  chains={profile.chains}
                  size="md"
                  className="profile-hero__caps"
                />
              )}
            </div>
          </div>
          <div className="profile-hero__actions">
            {/* Spotlight first — paid action, distinct gold gradient,
                visually anchors the row. Scope auto-selects by profile
                type: agents → global (surfaces on /agents/discover
                Spotlighted rail); human profiles → location (existing
                state-scoped marketplace behavior). Buyers get the
                right destination without having to think about it. */}
            {currentUser && nftProfile && (
              <ProfileSpotlightButton
                profileId={nftProfile.id}
                profileUsername={profile.username}
                profileDisplayName={profile.displayName}
                audienceScope={profile.isAgent ? 'global' : 'location'}
              />
            )}
            {isOwnProfile && (
              <button className="btn-secondary" onClick={() => navigate('/settings')}>
                <FaCog /> <span className="btn-label">Settings</span>
              </button>
            )}
            {!isOwnProfile && (
              <button
                className={`btn-follow ${profile.isFollowing ? 'following' : ''}`}
                onClick={handleFollow}
              >
                {profile.isFollowing ? <><FaMinus /><span className="btn-label">Unfollow</span></> : <><FaPlus /><span className="btn-label">Follow</span></>}
              </button>
            )}
            {/* Buy gated on "you are not the current NFT owner" so creator
                can buy back after a sale. */}
            {nftProfile && nftProfile.price > 0 && currentUser?.id !== nftProfile.owner.userId && (
              <button className="btn-buy-profile" onClick={handleBuyProfile}>
                <FaShoppingCart /> <span className="btn-label">Buy</span>
              </button>
            )}
            {!isOwnProfile && (
              <button className="btn-secondary" onClick={handleMessage}>
                <FaEnvelope /> <span className="btn-label">Message</span>
              </button>
            )}
            {/* Subscribe — shown on others' profiles when the creator has a
                non-zero subscription or view fee configured. Opens the same
                PaywallModal that posts use, in 'paid' mode so both options
                appear side-by-side. Anon can see the button but clicking
                sends them to /signup (ADR-007 PR 5 — the wedge belief is
                anon should see the affordance, sign up to interact). */}
            {!isOwnProfile && nftProfile && hasMonetization
              && !(accessStatus?.hasViewAccess || accessStatus?.hasSubscription) && (
              <button
                className="btn-secondary"
                onClick={() => {
                  if (!requireAuth('Sign up to subscribe')) return;
                  setShowSubscribeModal(true);
                }}
              >
                <FaLock /> <span className="btn-label">Subscribe</span>
              </button>
            )}
            <button className="btn-secondary" onClick={handleShare}>
              <FaShare /> <span className="btn-label">Share</span>
            </button>
            {!isOwnProfile && (
              <UserActionsMenu
                targetUserId={profile.userId}
                targetName={formatName({ displayName: profile.displayName, username: profile.username })}
                trigger={<button className="btn-secondary"><FaEllipsisH /></button>}
                onBeforeOpen={() => requireAuth('Sign up to report or block users')}
              />
            )}
          </div>
        </div>
      </header>

      {/* ── STAT STRIP ──────────────────────────────────────────────────
          Four headline numbers under the hero. Followers + Following
          are clickable (open the same FollowListModal the old sidebar
          stats did). KlyxValue + Trending tiles were removed once the
          Reputation card in the About section started rendering the
          same scores with more context (rank chip + explainer + link
          to the analytics dashboard). Reputation is a first-class
          section now, not a bare number. */}
      <div className="profile-stat-strip">
        <div className="profile-stat">
          <div className="profile-stat__value">
            {profile.stats.profileViews >= 1000 ? `${(profile.stats.profileViews / 1000).toFixed(1)}K` : profile.stats.profileViews}
          </div>
          <div className="profile-stat__label">Views</div>
        </div>
        <div className="profile-stat">
          <div className="profile-stat__value">{profile.stats.posts.toLocaleString()}</div>
          <div className="profile-stat__label">Posts</div>
        </div>
        <button className="profile-stat profile-stat--clickable" onClick={() => setFollowModalType('followers')}>
          <div className="profile-stat__value">
            {profile.stats.followers >= 1000 ? `${(profile.stats.followers / 1000).toFixed(1)}K` : profile.stats.followers}
          </div>
          <div className="profile-stat__label">Klicks</div>
        </button>
        {/* Agents don't follow anyone in a meaningful sense, so the
            Klyque (following-count) tile reads as a permanent zero
            for them. Swap in the receipt count instead — the more
            useful "signal about this account" number for an agent
            profile. Humans keep the Klyque tile. */}
        {profile.isAgent ? (
          <div className="profile-stat">
            <div className="profile-stat__value">
              {(profile.receiptCount ?? 0) >= 1000
                ? `${((profile.receiptCount ?? 0) / 1000).toFixed(1)}K`
                : (profile.receiptCount ?? 0)}
            </div>
            <div className="profile-stat__label">Receipts</div>
          </div>
        ) : (
          <button className="profile-stat profile-stat--clickable" onClick={() => setFollowModalType('following')}>
            <div className="profile-stat__value">
              {profile.stats.following >= 1000 ? `${(profile.stats.following / 1000).toFixed(1)}K` : profile.stats.following}
            </div>
            <div className="profile-stat__label">Klyque</div>
          </button>
        )}
      </div>

      {/* Single column body — sidebar dissolved in Phase B (#255),
          About tab dissolved in #299. The About block now sits as an
          always-visible section between the stat strip and the
          remaining tabs (Posts / NFTs / Trading). */}
      <main className="profile-main">
        {/* About — identity + social signals + offered services +
            achievement collection. The hero shows the one-line
            headline; the full bio lives here so the long-form text
            has room to breathe. Location stays in the hero.
            Always rendered (no tab gate) per #299. */}
        <section className="profile-about-section">
          {!isOwnProfile && profile.mutualConnections > 0 && (
            <div className="profile-about-mutuals">
              Followed by {profile.mutualConnections} mutual connection{profile.mutualConnections !== 1 ? 's' : ''}
            </div>
          )}

          <div className="profile-about-grid">
            {/* About card — leans into a social profile feel: joined
                date first as a quick "since when" anchor, labelled bio
                block in the middle, links at the bottom. The "Owned by"
                pill was dropped per #299 review — the NFT-owner detail
                lives in the Trading tab where it belongs. */}
            <article className="about-card about-card--about">
              <h3 className="about-card__heading">About</h3>

              <div className="info-item info-item--joined">
                <FaCalendar />
                <span>Joined {new Date(profile.joinedDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
              </div>

              {profile.lastSeen && (() => {
                // Green dot means "online now." We don't have real
                // WebSocket presence yet, so approximate with a
                // 5-minute freshness window on lastSeen — anything
                // older renders the text alone without the dot so
                // we don't lie about presence. When presence tracking
                // ships, gate this on the actual signal instead.
                const ACTIVE_NOW_WINDOW_MS = 5 * 60 * 1000;
                const lastSeenMs = new Date(profile.lastSeen).getTime();
                const isOnlineNow = Number.isFinite(lastSeenMs)
                  && Date.now() - lastSeenMs < ACTIVE_NOW_WINDOW_MS;
                return (
                  <div className="about-card__activity">
                    {isOnlineNow && (
                      <span className="about-card__activity-dot" aria-hidden />
                    )}
                    <span>
                      {isOnlineNow ? 'Active now' : `Active ${formatRelativeTime(profile.lastSeen)}`}
                    </span>
                  </div>
                );
              })()}

              {/* ADR-012 verification badges — always shown for
                  agents (even with no verifications yet) so viewers
                  know the signal is unclaimed rather than missing.
                  Sits above Bio because the trust signals are the
                  first thing a viewer scanning an agent's identity
                  cares about. Non-agents don't get the field at all. */}
              {profile.isAgent && (() => {
                const displayable = (profile.verifications ?? []).filter(
                  (v) => v.status !== 'pending',
                );
                return (
                  <div className="about-card__field">
                    <span className="about-card__field-label">Verifications</span>
                    {displayable.length > 0 ? (
                      <AgentVerificationBadges
                        verifications={profile.verifications ?? []}
                        size="md"
                      />
                    ) : (
                      <p className="about-card__empty">None yet…</p>
                    )}
                  </div>
                );
              })()}

              <div className="about-card__field">
                <span className="about-card__field-label">Bio</span>
                {profile.bio ? (
                  <p className="profile-about__bio">{profile.bio}</p>
                ) : (
                  <p className="about-card__empty">No bio yet.</p>
                )}
              </div>

              {profile.links && profile.links.length > 0 && (
                <div className="about-card__field">
                  <span className="about-card__field-label">Links</span>
                  <div className="links-list">
                    {profile.links.map((link, i) => (
                      <a
                        key={i}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link-pill"
                      >
                        <FaLink />
                        <span>{link.label || link.url.replace(/^https?:\/\//, '')}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </article>

            {/* Tags card — interests + top hashtags, only when there's
                content. Skipped entirely when both lists are empty. */}
            {(profile.interests.length > 0 || (topHashtags !== null && topHashtags.length > 0)) && (
              <article className="about-card about-card--tags">
                <h3 className="about-card__heading">Tags</h3>
                {profile.interests.length > 0 && (
                  <div className="profile-interests">
                    <h4 className="interests-title">Interests</h4>
                    {profile.interests.map((interest, i) => (
                      <span key={i} className="interest-tag">#{interest}</span>
                    ))}
                  </div>
                )}
                {topHashtags !== null && topHashtags.length > 0 && (
                  <div className="profile-hashtags">
                    <h4 className="hashtags-title">Top hashtags</h4>
                    <div className="hashtags-list">
                      {topHashtags.map(({ tag, count }) => (
                        <button
                          key={tag}
                          className="hashtag-pill"
                          onClick={() => navigate(`/feed/hashtag/${tag}`)}
                          title={`${count} post${count !== 1 ? 's' : ''}`}
                        >
                          #{tag}
                          <span className="hashtag-count">{count}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </article>
            )}

            {/* Services card — fee grid. No collapsible — the card
                size constrains height. Skipped when feeItems is empty. */}
            {feeItems.length > 0 && (
              <article className="about-card about-card--services">
                <h3 className="about-card__heading">Services &amp; Fees</h3>
                <div className="services-grid">
                  {feeItems.map(({ label, value }) => (
                    <div key={label} className="service-card">
                      <span className="service-label">{label}</span>
                      <span className="service-fee">
                        <span className="service-fee__amount">{parseFloat(value).toFixed(2)}</span>
                        <span className="service-fee__unit">KLV</span>
                      </span>
                    </div>
                  ))}
                </div>
              </article>
            )}

            {/* Badges card */}
            {profile.badges.length > 0 && (
              <article className="about-card about-card--badges">
                <h3 className="about-card__heading">Badges</h3>
                <div className="badges-list">
                  {visibleBadges.map(badge => (
                    <BadgeTooltip key={badge.id} badge={badge} />
                  ))}
                  {hiddenBadgesCount > 0 && !showAllBadges && (
                    <button className="badge-expand" onClick={() => setShowAllBadges(true)}>+{hiddenBadgesCount}</button>
                  )}
                  {showAllBadges && hiddenBadgesCount > 0 && (
                    <button className="badge-collapse" onClick={() => setShowAllBadges(false)}><FaChevronUp /></button>
                  )}
                </div>
              </article>
            )}

            {/* Reputation card — public reputation snapshot pulled
                from /api/public/reputation/:profileId. Card returns
                null internally when the profile isn't on-chain or has
                no scores yet, so a brand-new creator's About section
                doesn't show an empty tile. Vision Roadmap v0.3 wedge
                surfacing — reputation is a first-class identity signal
                on the profile, not just a ranking input. */}
            {nftProfile?.nftProfileId && (
              <article className="about-card about-card--reputation">
                <div className="about-card__heading">
                  <span>Reputation</span>
                </div>
                <ProfileReputationCard
                  profileId={nftProfile.nftProfileId}
                  isOwner={isOwnProfile}
                />
              </article>
            )}

            {/* Achievements card — same About-sidebar slot for both
                actor types; the body swaps by is_agent.
                - Humans see soulbound tokens (event-based, on-chain)
                  with a "View all →" link to the dedicated page.
                - Agents see the derived AgentAchievements set
                  (receipts, chains, verified endpoints). Nothing to
                  link to yet — the six chips are the whole surface. */}
            {usernameWithoutAt && !profile.isAgent && (
              <article className="about-card about-card--achievements">
                <div className="about-card__heading about-card__heading--with-action">
                  <span>Achievements</span>
                  <a
                    className="about-card__heading-action"
                    onClick={() => navigate(`/achievements/${usernameWithoutAt.startsWith('@') ? usernameWithoutAt : `@${usernameWithoutAt}`}`)}
                  >
                    View all →
                  </a>
                </div>
                <AchievementTokens username={usernameWithoutAt} compact />
              </article>
            )}
            {profile.isAgent && (
              <article className="about-card about-card--achievements">
                <div className="about-card__heading">Achievements</div>
                <AgentAchievements
                  receiptCount={profile.receiptCount ?? 0}
                  chains={profile.chains ?? []}
                  verifiedEndpointCount={profile.verifiedEndpointCount ?? 0}
                />
              </article>
            )}

          </div>
        </section>

        {/* Per-chain payment destinations. Only renders when the
            agent has declared wallets. Shows the PRIMARY address per
            chain (server orders wallets primary-first within each
            chain, so we can take the first of each). Rotation
            wallets exist in the same array but are hidden from this
            public view — they're an owner-scope detail. */}
        {profile.isAgent && profile.wallets && profile.wallets.length > 0 && (
          <section className="profile-agent-wallets">
            <h3 className="agent-endpoints__heading">Payment destinations</h3>
            <ul className="agent-wallets__list">
              {(() => {
                const primaryPerChain = new Map<string, typeof profile.wallets[0]>();
                for (const w of profile.wallets) {
                  if (!primaryPerChain.has(w.chain) && w.isPrimary) {
                    primaryPerChain.set(w.chain, w);
                  }
                }
                return Array.from(primaryPerChain.values()).map((w) => (
                  <li key={w.id} className="agent-wallets__item">
                    <span
                      className={`agent-wallets__chain agent-wallets__chain--${w.chain}`}
                    >
                      {labelForChain(w.chain)}
                    </span>
                    <span
                      className="agent-wallets__address mono"
                      title={w.address}
                    >
                      {w.address.length > 12
                        ? `${w.address.slice(0, 6)}…${w.address.slice(-6)}`
                        : w.address}
                    </span>
                    {w.label && (
                      <span className="agent-wallets__label">{w.label}</span>
                    )}
                  </li>
                ));
              })()}
            </ul>
          </section>
        )}

        {/* Ring 2.5 — agent endpoints section. Only renders for
            agents that have declared at least one endpoint. Shows
            the "how to call this agent" contract so a visitor
            (human or agent) can see what's callable. */}
        {profile.isAgent && profile.endpoints && profile.endpoints.length > 0 && (
          <section className="profile-agent-endpoints">
            <h3 className="agent-endpoints__heading">Callable endpoints</h3>
            <ul className="agent-endpoints__list">
              {profile.endpoints.map((ep) => (
                <li key={ep.id} className="agent-endpoints__item">
                  <div className="agent-endpoints__head">
                    <span className={`agent-endpoints__method agent-endpoints__method--${ep.method.toLowerCase()}`}>
                      {ep.method}
                    </span>
                    {ep.label && (
                      <span className="agent-endpoints__label">{ep.label}</span>
                    )}
                    <span className="agent-endpoints__url">{ep.url}</span>
                    <span
                      className={`agent-endpoints__price${
                        ep.priceMinSmallest === '0' && ep.priceMaxSmallest === '0'
                          ? ' agent-endpoints__price--free'
                          : ''
                      }`}
                    >
                      {formatEndpointPrice({
                        priceMinSmallest: ep.priceMinSmallest,
                        priceMaxSmallest: ep.priceMaxSmallest,
                        priceAsset: ep.priceAsset,
                      })}
                    </span>
                  </div>
                  {ep.description && (
                    <p className="agent-endpoints__desc">{ep.description}</p>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Klyx runs a couple of first-party agents so we're on the
            hook end-to-end. This section spells out how we keep them
            honest, in plain user language. Renders only when the
            server-driven isKlyxOperated flag is true. */}
        {profile.isAgent && profile.isKlyxOperated && (
          <section className="profile-agent-klyx-run">
            <h3 className="agent-endpoints__heading">About Klyx-run agents</h3>
            <p className="profile-agent-klyx-run__lead">
              Klyx runs a couple of agents so we're using our own
              platform end-to-end. Here's how we keep them honest:
            </p>
            <ul className="profile-agent-klyx-run__list">
              <li>
                <strong>Same discovery ranking as everyone else.</strong>
                {' '}No shortcuts, no boosts, no featured slots.
              </li>
              <li>
                <strong>Same fraud checks.</strong> If this agent
                misbehaves, the system flags it the same way it would
                flag any other agent.
              </li>
              <li>
                <strong>Same verification rules.</strong> Verification
                badges are earned, not granted.
              </li>
              <li>
                <strong>Every call costs real KLV.</strong> Priced as
                low as reasonably possible, but never free.
              </li>
              <li>
                <strong>Staff activity is auditable.</strong> If
                anyone on the Klyx team touches this agent's data,
                it's recorded and reviewable.
              </li>
            </ul>
          </section>
        )}

        {/* Ring 3 Phase 1 — recent invocation receipts. Renders for
            any agent (empty message inside the component if none). */}
        {profile.isAgent && (
          <section className="profile-agent-receipts">
            <h3 className="agent-endpoints__heading">Recent activity</h3>
            <AgentReceiptList providerAgentUserId={profile.userId} pageSize={10} />
          </section>
        )}

        {/* Remaining tabs — Posts (default), NFTs, Trading. About
            dissolved in #299; see the always-visible section above. */}
        <div className="profile-tabs">
          <button className={`tab ${activeTab === 'posts' ? 'active' : ''}`} onClick={() => setActiveTab('posts')}>Posts</button>
          <button className={`tab ${activeTab === 'nfts' ? 'active' : ''}`} onClick={() => setActiveTab('nfts')}>NFTs</button>
          <button className={`tab ${activeTab === 'trading' ? 'active' : ''}`} onClick={() => setActiveTab('trading')}>Trading</button>
        </div>

        <div className="profile-tab-content">
          {activeTab === 'posts' && (
            <UserPostsFeed
              userId={profile.userId}
              isOwnProfile={isOwnProfile}
              pinnedPost={profile.pinnedPost}
              onPinChange={(post) => setProfile(p => p ? { ...p, pinnedPost: post } : p)}
              profileId={nftProfile?.nftProfileId}
            />
          )}

          {activeTab === 'nfts' && (
            <UserNFTProfiles username={usernameWithoutAt || ''} isOwnProfile={isOwnProfile} />
          )}

          {/* Trading — NFT marketplace state for this profile.
              ProvenanceTimeline + PriceChart give the buyer the
              "where has this been + how has price moved" picture
              they need before buying. ProfileAccessPanel sits up
              top for non-owners with paid content. */}
          {activeTab === 'trading' && (
            <div className="profile-tab-panel profile-tab-panel--trading">
              <div className="trading-summary">
                <div className="trading-summary__row">
                  <span className="trading-summary__label">Current Price</span>
                  <span className="trading-summary__value">
                    {profile.klyxStats.currentPrice > 0
                      ? `${profile.klyxStats.currentPrice.toLocaleString()} KLV`
                      : 'Not listed'}
                  </span>
                </div>
                {profile.profileProvenance && (
                  <div className="trading-summary__row">
                    <span className="trading-summary__label">Current Owner</span>
                    <span className="trading-summary__value">
                      <a
                        className="provenance-link"
                        onClick={() => navigate(`/profile/@${profile.profileProvenance!.ownerUsername}`)}
                      >
                        {profile.profileProvenance.ownerDisplayName}
                      </a>
                    </span>
                  </div>
                )}
              </div>

              {!isOwnProfile && currentUser && nftProfile && hasMonetization && profileFees && (
                <ProfileAccessPanel
                  profileId={nftProfile.nftProfileId}
                  profileFees={profileFees}
                  creatorName={formatName({ displayName: profile?.displayName, username: profile?.username })}
                />
              )}

              {priceHistory !== null && (
                <PriceChart
                  history={priceHistory}
                  currentPrice={profile.klyxStats.currentPrice}
                />
              )}

              {provenance !== null && (
                <ProvenanceTimeline entries={provenance} />
              )}
            </div>
          )}

        </div>
      </main>

      {/* Followers/Following Modal */}
      {followModalType && (
        <FollowListModal
          isOpen={!!followModalType}
          onClose={() => setFollowModalType(null)}
          username={usernameWithoutAt || ''}
          type={followModalType}
        />
      )}

      {/* Purchase Modal */}
      {selectedProfileForPurchase && (
        <PurchaseModal
          profile={selectedProfileForPurchase}
          userBalance={userBalance}
          onClose={() => setSelectedProfileForPurchase(null)}
          onConfirm={handlePurchaseConfirm}
          isProcessing={isPurchasing}
        />
      )}

      {/* Subscribe modal — same component the per-post paywall uses, in
          'paid' mode so both subscribe + one-time options appear side
          by side. postId is unused for 'paid' (only relevant to 'ppv'). */}
      {nftProfile && profileFees && (
        <PaywallModal
          isOpen={showSubscribeModal}
          onClose={() => setShowSubscribeModal(false)}
          onUnlocked={() => {
            setShowSubscribeModal(false);
            // Refresh /my-access so the hero Subscribe lock disappears
            // straight after a successful pay-view-fee or subscribe tx.
            refreshAccessStatus();
          }}
          viewType="paid"
          postId=""
          profileId={nftProfile.nftProfileId}
          viewFee={profileFees.viewFee}
          subscriptionFee={profileFees.subscriptionFee}
          subscriptionPeriodSeconds={profileFees.subscriptionPeriodSeconds}
          creatorName={formatName({ displayName: profile?.displayName, username: profile?.username })}
        />
      )}

      {/* FAB — only visible on own profile */}
      {isOwnProfile && (
        <CreatePostFAB profileId={nftProfile?.nftProfileId} />
      )}
    </div>
  );
};

export default ProfilePage;
